import VerticalStepper from 'app/views/components/stepper-component'
import { useEffect, useRef, useState } from 'react'
import ManageLoanLenders, {
    ChildManageLendersRef,
} from 'app/views/components/lenders/manage-loan-lenders'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import UpdatedLoanModal, {
    ChildLoanRef,
} from 'app/views/pages/deals-management/loans/loan-form-presentation'
import { isNotEmpty, useForm } from '@mantine/form'
import { useDispatch, useSelector } from 'react-redux'
import { loadWireInstructions } from 'app/state/ducks/wire-instructions/thunks'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import { getWireInstructions } from 'app/state/ducks/wire-instructions/selectors'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { EntityParams } from 'app/models/entity-params'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { FacilityParams } from 'app/models/facility-params'
import { LoanParams } from 'app/models/loan-params'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { getLoanAmounts } from 'app/state/ducks/loans/selectors'
import { LoanAmountsData } from 'app/models/loan-params'
import {
    formatDateToUTC,
    stringToDate,
    toOptionalFxRate,
    validateOptionalFxRate,
} from 'app/utils/util-functions'
import { arePikFieldsApplicable } from './utilities/pik-config-utils'
import { deriveAllInRate } from 'app/utils/rate-rounding'
import { usePrerequisiteReads } from 'app/hooks/usePrerequisiteReads'
import { CustomLoader } from 'app/views/components/loader/loader'
import { ErrorNotification } from 'app/views/components/notifications/notification'

type Props = {
    facility: FacilityParams | undefined
    loan?: LoanParams | undefined
    onClose: () => void
    step?: number
    isAmendment?: boolean
    isEdit?: boolean
}

interface CustomStepContentProps {
    onNext: () => void
    onPrevious: () => void
}

export const UpdatedLoanScreen: React.FC<Props> = ({
    loan,
    onClose,
    facility,
    step,
    isAmendment,
    isEdit,
}) => {
    const [updated_loan, setLoan] = useState(loan)

    // Reference data the loan form reads: the wire instructions it offers
    // (and keeps on the loan — an edit saved while that list is missing
    // would clear the loan's instruction key), the index rate options, the
    // entities and the deals. The app header loads most of it only when its
    // own form opens; the Servicing page opens this form with nothing behind
    // it. So the form opens once every list it lacks has loaded, and not at
    // all when one cannot be: a failed read is not an empty list.
    const lookupDispatch = useDispatch()
    const wireInstructionsKnown = useSelector(getWireInstructions)
    const indexesKnown = useSelector(getIndexRateOptions)
    const entitiesKnown = useSelector(getEntities)
    const dealsKnown = useSelector(getDeals)
    const lookupsReady = usePrerequisiteReads(
        () => [
            ...(wireInstructionsKnown?.length ? [] : [lookupDispatch(loadWireInstructions())]),
            ...(indexesKnown?.length ? [] : [lookupDispatch(loadIndexRateOptions())]),
            ...(entitiesKnown?.length ? [] : [lookupDispatch(loadEntities())]),
            ...(dealsKnown?.length ? [] : [lookupDispatch(loadAllDeals())]),
        ],
        () => {
            ErrorNotification({
                title: 'Could not open',
                message: 'The lists the loan form needs could not be loaded. Please try again.',
            })
            onClose()
        },
    )

    // Sync updated_loan with loan prop changes
    useEffect(() => {
        setLoan(loan)
    }, [loan])

    const LoanContent: React.FC<CustomStepContentProps> = ({
        onNext,
    }) => {
        const childRef = useRef<ChildLoanRef>(null)
        const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true)
        const [loading, setLoading] = useState(false);
        const [validationError, setValidationError] = useState<string | null>(null);
        const [pikValidationError, setPikValidationError] = useState<string | null>(null);
        const [hasInteractedWithFields, setHasInteractedWithFields] = useState(false);

        const handleSaveFacilityAndExit = async () => {
            if (childRef.current) {
                setLoading(true)
                await childRef.current.handleClick()
                setLoading(false)
                onClose()
            }
        }

        const handleSaveFacilityAndNext = async () => {
            if (childRef.current) {
                setLoading(true)
                await childRef.current.handleClick()
                setLoading(false)
                onNext()
            }
        }

        const wireInstructionList = useSelector(getWireInstructions)
        const entities = useSelector(getEntities)
        const businessDate = useSelector(getBusinessDate)
        const allIndexes = useSelector(getIndexRateOptions)
        const loanAmounts = useSelector(getLoanAmounts)
        // useForm's validate closures capture first-render scope; loanAmounts loads
        // async, so the validator reads it through a ref to stay fresh.
        const loanAmountsRef = useRef<LoanAmountsData[]>([])
        useEffect(() => {
            loanAmountsRef.current = loanAmounts ?? []
        }, [loanAmounts])

        // Custom validation function for rate and maturity changes
        const validateChanges = (values: any) => {
            // Only apply this validation for edits or amendments
            if (!isEdit && !isAmendment) {
                return null
            }

            // Get original values from the loan
            const originalInterestBaseRate = Number(updated_loan?.interestBaseRate || 0)
            const originalMargin = Number(updated_loan?.margin || 0)
            const originalCasValue = Number(updated_loan?.casValue || 0)
            const originalEndDate = updated_loan?.endDate

            // Get current form values
            const currentInterestBaseRate = Number(values.interestBaseRate || 0)
            const currentMargin = Number(values.margin || 0)
            const currentCasValue = Number(values.casValue || 0)
            const currentEndDate = values.endDate ? formatDateToUTC(values.endDate) : null

            // Check if at least one rate has changed
            const interestBaseRateChanged = currentInterestBaseRate !== originalInterestBaseRate
            const marginChanged = currentMargin !== originalMargin
            const casValueChanged = currentCasValue !== originalCasValue
            const rateChanged = interestBaseRateChanged || marginChanged || casValueChanged

            // Check if maturity date has changed
            const maturityChanged = currentEndDate !== originalEndDate

            // At least one of rates OR maturity must change
            if (!rateChanged && !maturityChanged && !values.amendmentDate) {
                return 'At least one of the following must be updated: Base Rate, Margin, CAS, or Maturity Date'
            }

            return null
        }

        const validatePikFields = (values: any) => {

            const shouldValidatePik =
                (facility?.pikOption === 'PIK_Optional' && values.pikToggle === 'On') ||
                facility?.pikOption === 'PIK_Scheduled'

            if (!shouldValidatePik) {
                return null
            }

            const pikInterest = Number(values.pikInterestPercent || 0)
            const cashInterest = Number(values.cashInterestPercent || 0)
            const allInRateWithPik = Number(values.allInRate || 0)
            const sum = pikInterest + cashInterest
            const tolerance = 0.01
            const diff = Math.round(Math.abs(sum - allInRateWithPik) * 100) / 100

            if (diff >= tolerance) {
                return 'PIK Interest % + Cash Interest % must equal All-In Rate'
            }

            return null
        }



        const form: any = useForm({
            initialValues: {
                id: updated_loan?.id ?? undefined,
                transactionId: updated_loan?.transactionId ?? undefined,
                facilityId: cleanUUID(facility?.id ?? '') ?? '',
                amount: Number(updated_loan?.amount) ?? 0,
                currency:
                    updated_loan?.currency ?? facility?.baseCurrency ?? facility?.baseCurrency,
                // Null rather than 0: the ledger wants no rate at all for a
                // loan in the facility's base currency, and 0 is never valid.
                // Coerced because DAML serializes Decimals as strings.
                fxRate:
                    updated_loan?.fxRate != null ? Number(updated_loan.fxRate) : null,
                dayBasis: updated_loan?.dayBasis ?? '',
                interestBaseRate: Number(updated_loan?.interestBaseRate) ?? 0,
                indexOption: updated_loan?.indexOption.id ?? '',
                interestBaseRateWithRounding:
                    Number(updated_loan?.interestBaseRateWithRounding) ?? 0,
                rounding: updated_loan?.rounding ?? '',
                endDate: updated_loan?.endDate ? stringToDate(updated_loan.endDate) : null,
                startDate: updated_loan?.startDate
                    ? stringToDate(updated_loan.startDate)
                    : businessDate,
                isSwingLine: updated_loan?.isSwingLine == true ? 'true' : 'false',
                isNonProRata: updated_loan?.isNonProRata == true ? 'true' : 'false',
                margin: Number(updated_loan?.margin) ?? 0,
                pikPremium: Number(updated_loan?.pikPremium) ?? 0,
                pikInterestPercent: Number(updated_loan?.pikInterestPercent) ?? 0,
                cashInterestPercent: Number(updated_loan?.cashInterestPercent) ?? 0,
                pikToggle: updated_loan ? (updated_loan?.pikPremium || updated_loan?.pikInterestPercent ? 'On' : 'Off') : '',
                allInRate: deriveAllInRate({
                    interestBaseRate: updated_loan?.interestBaseRate,
                    rounding: updated_loan?.rounding,
                    margin: updated_loan?.margin,
                    casValue: updated_loan?.casValue,
                    pikPremium: updated_loan?.pikPremium,
                }).allInRate,
                amountDue: null,
                accrualInterestMaturityDate: null,
                casValue: Number(updated_loan?.casValue) ?? 0,
                contractPeriod: updated_loan?.contractPeriod ?? '',
                borrower: cleanUUID(updated_loan?.borrower?.id ?? '') ?? '',
                borrowerPaymentInstructions:
                    cleanUUID(updated_loan?.borrowerPaymentInstructions?.id) ?? '',
                hasError: false,
                ErrorMessage: '',
                lendersPositions: updated_loan?.lendersPositions ?? [],
                hasSuccessfulEntitySave: false,
                customer_token: '',
                readyForApproval: false,
                nextInterestPaymentDate: updated_loan?.nextInterestPaymentDate ?? null,
                nextPIKDate: updated_loan?.nextPIKDate ?? null,
                systemId: updated_loan?.systemId ?? null, // Include original loan's systemId
                agentLoanId: updated_loan?.agentLoanId ?? '',
                ...(isAmendment && {
                    amendmentType: (() => {
                        // For amendments, determine type based on what fields are changing
                        // This will be refined by the backend based on actual changes
                        return 'LoanAmendment' // Default for amendments
                    })(),
                    amendmentLenders: updated_loan?.lendersPositions ?? [],
                    amendmentDate: (() => {
                        // If editing existing draft amendment, use its date
                        if (updated_loan?.amendmentDate) {
                            return stringToDate(updated_loan.amendmentDate)
                        }

                        // For new amendments, check if business date is after loan end date
                        const loanEndDate = updated_loan?.endDate ? stringToDate(updated_loan.endDate) : null
                        if (loanEndDate && businessDate > loanEndDate) {
                            // Business date is after loan end date, leave blank to force user selection
                            return null
                        }

                        // Business date is valid, use it as default
                        return businessDate
                    })()
                }),
                ...(isEdit && {
                    amendmentType: (() => {
                        // For edits, determine type based on what fields are changing
                        // This will be refined by the backend based on actual changes
                        return 'LoanEdit' // Default for edits
                    })(),
                    amendmentLenders: updated_loan?.lendersPositions ?? [],
                    amendmentDate: updated_loan?.startDate ? stringToDate(updated_loan.startDate) : businessDate
                })
            },

            transformValues: values => {
                const borrowerPaymentInstructions = wireInstructionList.find(
                    (wireInstruction: any) =>
                        values.borrowerPaymentInstructions == wireInstruction.id
                )
                const borrower: EntityParams | undefined = entities.find(
                    entity => entity.id == form.values.borrower
                )

                const indexOption = allIndexes.find(
                    option => option.id === values.indexOption
                )

                // For amendments, we need to convert numbers to strings and ensure required fields are present
                const transformedValues = {
                    ...values,
                    agentLoanId: values.agentLoanId || null,
                    startDate: formatDateToUTC(values.startDate),
                    endDate: formatDateToUTC(values.endDate as Date),
                    borrower: borrower ? { id: borrower.id, admin: borrower.maker } : null,
                    borrowerPaymentInstructions: borrowerPaymentInstructions
                        ? {
                            id: borrowerPaymentInstructions.id,
                            admin: borrowerPaymentInstructions.public,
                        }
                        : null,
                    facilityId: {
                        id: facility?.id,
                        admin: facility?.accountManagementAdmin,
                    },
                    isSwingLine: values.isSwingLine == 'false' ? false : true,
                    isNonProRata: values.isNonProRata == 'false' ? false : true,
                    lendersPositions: [],
                    fxRate: toOptionalFxRate(values.fxRate),
                    rounding: values.rounding != '' ? values.rounding : null,
                    dayBasis: values.dayBasis != '' ? values.dayBasis : null,
                    indexOption: {
                        id: indexOption?.id ?? '',
                        admin: indexOption?.public ?? '',
                    },
                    contractPeriod:
                        values.contractPeriod != '' ? values.contractPeriod : null,
                    allInRate: values.allInRate !== 0.0
                        ? values.allInRate - (Number(values.pikPremium) || 0)
                        : null,
                    allInRateWithPikPremium: values.allInRate !== 0.0 ? values.allInRate : null,
                    interestBaseRate:
                        values.interestBaseRate !== 0.0 ? values.interestBaseRate : null,
                    interestBaseRateWithRounding:
                        values.interestBaseRateWithRounding !== 0.0
                            ? Number(values.interestBaseRateWithRounding)
                            : null,
                    pikPremium: arePikFieldsApplicable(facility?.pikOption, values.pikToggle === 'On')
                        ? (values.pikPremium != null ? Number(values.pikPremium) : null)
                        : null,
                    pikInterestPercent: arePikFieldsApplicable(facility?.pikOption, values.pikToggle === 'On')
                        ? Number(values.pikInterestPercent || 0)
                        : null,
                    cashInterestPercent: arePikFieldsApplicable(facility?.pikOption, values.pikToggle === 'On')
                        ? Number(values.cashInterestPercent || 0)
                        : null,
                }

                if (isAmendment) {
                    return {
                        ...transformedValues,
                        amountDue: values.amount,
                        accrualInterestMaturityDate: 0, // Initialize accrued interest to 0 for new amendments
                        amendmentDate: values.amendmentDate ? formatDateToUTC(values.amendmentDate) : undefined,
                        amendmentType: 'LoanAmendment',
                        systemId: updated_loan?.systemId ?? null,
                    }
                }

                if (isEdit) {
                    return {
                        ...transformedValues,
                        amountDue: values.amount,
                        accrualInterestMaturityDate: 0, // Initialize accrued interest to 0 for new edits
                        amendmentDate: undefined, // Don't submit amendment date for edits, back end will handle undefined value and treat as edit
                        amendmentType: 'LoanEdit',
                        systemId: updated_loan?.systemId ?? null,
                    }
                }

                return transformedValues
            },
            validate: {
                currency: isNotEmpty('Currency cannot be empty'),
                facilityId: isNotEmpty('Facility cannot be empty'),
                // Only enforced while the field is visible (loan currency differs
                // from the facility base currency). The rate itself is optional —
                // only a value the user entered has to be positive.
                fxRate: (value: number | null, values: any) => {
                    if (!facility?.baseCurrency || values.currency === facility.baseCurrency) {
                        return null
                    }
                    return validateOptionalFxRate(value)
                },
                pikToggle: (value: string) => {
                    if (facility?.pikOption === 'PIK_Optional' && !value) {
                        return 'PIK (On/Off) selection is required for PIK Optional facilities'
                    }
                    return null
                },
                startDate: (value: Date) => {
                    if (!value) {
                        return 'Start Date Must Be Set'
                    }
                    if (facility && value < stringToDate(facility?.startDate)) {
                        return 'The Loan Start Date must be equal to or later than the Facility Start Date.'
                    }
                    return null
                },
                amendmentDate: (value: Date | null | undefined, values: any) => {
                    // No validation needed for loan edits (LoanEdit) since the field is disabled
                    if (!isAmendment) {
                        return null
                    }

                    // Amendment date is required for amendments
                    if (!value) {
                        return 'Amendment date is required for loan amendments.'
                    }

                    // Convert dates to midnight UTC for comparison to avoid timezone issues
                    const amendmentDateUTC = new Date(value.getFullYear(), value.getMonth(), value.getDate())

                    // Get loan start and end dates for range validation
                    const loanStartDate = values.startDate || (updated_loan?.startDate ? stringToDate(updated_loan.startDate) : null)
                    const loanEndDate = values.endDate || (updated_loan?.endDate ? stringToDate(updated_loan.endDate) : null)

                    if (!loanStartDate) {
                        return 'Cannot validate amendment date: loan start date is required.'
                    }

                    if (!loanEndDate) {
                        return 'Cannot validate amendment date: loan end date is required.'
                    }

                    const loanStartDateUTC = new Date(loanStartDate.getFullYear(), loanStartDate.getMonth(), loanStartDate.getDate())
                    const loanEndDateUTC = new Date(loanEndDate.getFullYear(), loanEndDate.getMonth(), loanEndDate.getDate())

                    // Check if amendment date equals the CURRENT period start (not allowed for
                    // amendments — a LoanEdit applies from the current start date, use that instead)
                    if (amendmentDateUTC.getTime() === loanStartDateUTC.getTime()) {
                        return 'Amendment date cannot be the loan start date. For corrections to initial loan booking, use Edit instead of Amendment.'
                    }

                    // Lower bound is the ORIGINAL loan start (earliest loan-amounts history
                    // entry), not the current startDate: rollovers move startDate forward, and
                    // a backdated amendment before the rollover is legitimate (reprocessing
                    // replays the rollover). Falls back to the current startDate while the
                    // history hasn't loaded or is empty.
                    const historyDates = (loanAmountsRef.current ?? [])
                        .map((e) => e.effectiveDate)
                        .sort()
                    const originalStartDateUTC = historyDates.length
                        ? (() => {
                            const d = stringToDate(historyDates[0])
                            return new Date(d.getFullYear(), d.getMonth(), d.getDate())
                        })()
                        : loanStartDateUTC

                    if (amendmentDateUTC < originalStartDateUTC) {
                        return 'Amendment date must be on or after the original loan start date.'
                    }

                    // Check if amendment date is after end date
                    if (amendmentDateUTC > loanEndDateUTC) {
                        return 'Amendment date must be before the loan end date.'
                    }

                    return null
                },
                amount: (value: number, values: any) => {
                    if (value <= 0.0) {
                        return 'Invalid amount'
                    }
                    if (!facility) {
                        return null
                    }
                    if (value > Number(facility?.amount)) {
                        return 'Amount cannot be greater than facility amount'
                    }
                    if (values.isSwingLine == 'false') {
                        return null
                    }
                    if (!facility.isSublimit) {
                        return null
                    }
                    const swingline = facility?.subLimits.find(
                        (sublimit: { subLimitsType: string }) => sublimit.subLimitsType === 'SwingLine'
                    )
                    if (!swingline) {
                        return null
                    }
                    if (value > Number(swingline.amount)) {
                        return 'Amount cannot be greater than swingline amount'
                    }
                    return null
                },
                endDate: (value, values) => {
                    if (!value) {
                        return 'Maturity Date Must Be Set'
                    }
                    if (values.startDate && value <= values.startDate) {
                        return 'Maturity Date must be after the Start Date'
                    }
                    if (facility && value && value > stringToDate(facility?.maturity)) {
                        return 'The Loan End Date must be equal to or before the Facility Maturity Date.'
                    }
                    return null
                },
            },
            validateInputOnBlur: true,
            validateInputOnChange: true,
        })

        // Check for validation error whenever form values change
        useEffect(() => {
            const rateError = validateChanges(form.values)
            const pikError = validatePikFields(form.values)
            // Only show validation error if user has interacted with rate fields or maturity field
            setValidationError(hasInteractedWithFields ? rateError : null)
            setPikValidationError(pikError) // Always show PIK error when present

            // Update save button disabled state - include custom validation
            const hasFormErrors = !form.isValid()
            const hasValidationError = rateError !== null || pikError !== null
            setIsSaveButtonDisabled(hasFormErrors || hasValidationError)
        }, [form.values, form.isValid(), hasInteractedWithFields])

        return (
            <div className="create-new">
                <UpdatedLoanModal
                    facility={facility}
                    loan={updated_loan}
                    form={form}
                    ref={childRef}
                    setSaveButtonDisabled={setIsSaveButtonDisabled}
                    setLoan={setLoan}
                    isAmendment={isAmendment}
                    isEdit={isEdit}
                    rateValidationError={validationError}
                    pikValidationError={pikValidationError}
                    onRateFieldInteraction={() => setHasInteractedWithFields(true)}
                />
                <div
                    className="btn-group"
                    style={{
                        marginTop: 50,
                        textAlign: 'right',
                        display: 'flex',
                        gap: '10px',
                    }}
                >
                    <PrimaryButton
                        onClick={() => handleSaveFacilityAndExit()}
                        className="form-button"
                        w="33%"
                        loading={loading}
                        disabled={isSaveButtonDisabled}
                    >
                        Save and Exit
                    </PrimaryButton>
                    <PrimaryButton
                        onClick={() => handleSaveFacilityAndNext()}
                        className="form-button"
                        loading={loading}
                        w="33%"
                        disabled={isSaveButtonDisabled}
                    >
                        Save and Proceed
                    </PrimaryButton>
                    <PrimaryButton
                        onClick={() => onClose()}
                        className="form-button"
                        loading={loading}
                        w="33%"
                    >
                        Exit
                    </PrimaryButton>
                </div>
            </div>
        )
    }

    const LenderAllocationContent: React.FC<CustomStepContentProps> = ({
        onNext,
        onPrevious,
    }) => {
        const childRef = useRef<ChildManageLendersRef>(null)
        const [loading, setLoading] = useState(false);

        const handleSaveFacilityAndExit = async () => {
            if (childRef.current) {
                setLoading(true)
                await childRef.current.handleClick()
                setLoading(false)
                onClose()
            }
        }

        const handlePrevious = () => {
            if (typeof onNext === 'function') {
                onPrevious()
            }
        }

        return (
            <div className="create-new">
                <ManageLoanLenders
                    loan={updated_loan as LoanParams}
                    ref={childRef}
                    setLoan={setLoan}
                    amendment={isAmendment}
                    asOfDate={
                        isAmendment
                            ? updated_loan?.amendmentDate
                            : isEdit
                                ? updated_loan?.startDate
                                : undefined
                    }
                />
                <div
                    className="btn-group"
                    style={{ marginTop: 10, display: 'flex', gap: '10px' }}
                >
                    <PrimaryButton
                        onClick={() => handleSaveFacilityAndExit()}
                        className="form-button"
                        loading={loading}
                        w="25%"
                    >
                        Save and Exit
                    </PrimaryButton>

                    <PrimaryButton
                        onClick={() => handlePrevious()}
                        className="form-button"
                        loading={loading}
                        w="25%"
                    >
                        Previous
                    </PrimaryButton>
                    <PrimaryButton
                        onClick={() => onClose()}
                        className="form-button"
                        loading={loading}
                        w="25%"
                    >
                        Close
                    </PrimaryButton>
                </div>
            </div>
        )
    }


    const steps = [
        {
            label: '1. Loan',
            content: (
                <LoanContent
                    onNext={function (): void {
                        throw new Error('Function not implemented.')
                    }}
                    onPrevious={function (): void {
                        throw new Error('Function not implemented.')
                    }}
                />
            ),
        },
        {
            label: 'i. Lender Allocation',
            content: (
                <LenderAllocationContent
                    onNext={function (): void {
                        throw new Error('Function not implemented.')
                    }}
                    onPrevious={function (): void {
                        throw new Error('Function not implemented.')
                    }}
                />
            ),
        },
    ]

    if (!lookupsReady) return <CustomLoader />

    return (
        <>
            {!updated_loan || Object.keys(updated_loan).length === 0 ? (
                //create
                <VerticalStepper
                    steps={steps}
                    disabledSteps={[1]}
                    step={step}
                />
            ) : (
                // edit — for a NEW amendment/edit on an approved loan (no draft
                // amendment yet, i.e. no amendmentType), lock the Lender
                // Allocations step: the draft (with its amendment date) must be
                // created via Save and Proceed first, so lender positions are
                // always resolved against a dated draft.
                <VerticalStepper
                    steps={steps}
                    disabledSteps={(isAmendment || isEdit) && !updated_loan?.amendmentType ? [1] : []}
                    step={step}
                />
            )}
        </>
    )
}
